import DeleteIcon from "@mui/icons-material/Delete";
import { Checkbox } from "@mui/material";
import { FormikProps } from "formik";
import { observer } from "mobx-react-lite";
import { useMemo, useState } from "react";
import { toast } from "react-toastify";
import { useDrawMapStore } from "../..";
import { IDealState } from "../../interfaces/IDeal";
import commonStyles from "../Common.module.css";
import PlusIcon from "../icons/PlusIcon";
import styles from "./DealParcelsInput.module.css";
import InputContainer from "./InputContainer";

function DealParcelsInput({ name, label, formik }: IDealParcelsInputProps) {
    const drawMapStore = useDrawMapStore();
    const [checkedIndexes, setCheckedIndexes] = useState<number[]>([]);
    const dealShapeApns = useMemo(() => formik.values[name] || [], [formik.values[name]]) as (string | null)[];

    const onDelete = (indexes: number[]) => {
        if (indexes.length === dealShapeApns.length) {
            toast.info("Unable to delete all items.");
            return;
        }

        setCheckedIndexes([]);
        formik.setFieldValue(name, dealShapeApns.filter((_, i) => !indexes.includes(i)));

        drawMapStore.setResult({
            ...drawMapStore.result!,
            features: drawMapStore.result!.features.filter((f, i) => !indexes.includes(i)),
        });
    }

    const drawShape = () => {
        drawMapStore.drawOneMoreDealShape(() => {
            formik.setFieldValue(name, [
                ...dealShapeApns,
                null,
            ]);
        });
    };

    return (
        <InputContainer label={label} name={name} formik={formik}>
            <div className={styles.inputContainer}>
                {dealShapeApns?.map((apn, index) => (
                    <ParcelRow
                        key={index}
                        value={apn || ""}
                        onChange={value => formik.setFieldValue(name, dealShapeApns!.map((a, i) => i === index ? value : a))}
                        isChecked={checkedIndexes.includes(index)}
                        onToggle={checked => setCheckedIndexes(indexes => checked ? [...indexes, index] : indexes.filter(i => i !== index))}
                        isSingle={dealShapeApns.length === 1}
                        onDelete={() => onDelete([index])}
                    />
                ))}
            </div>
            <div className={commonStyles.controls}>
                <div className={commonStyles.controlGroup}></div>
                <div className={commonStyles.controlGroup}>
                    {!!checkedIndexes.length && (
                        <div className={commonStyles.button} onClick={() => onDelete(checkedIndexes)}>
                            <DeleteIcon fontSize="small" className={commonStyles.clickableIcon} /> Delete Selected
                        </div>
                    )}
                    <div className={commonStyles.button} onClick={drawShape}>
                        <PlusIcon /> Draw Shape
                    </div>
                </div>
            </div>
        </InputContainer>
    );
}

export default observer(DealParcelsInput);

interface IDealParcelsInputProps {
    name: keyof IDealState;
    label: string;
    formik: FormikProps<IDealState>;
}

function ParcelRow({ value, onChange, isChecked, onToggle, isSingle, onDelete }: IParcelRowProps) {
    const [isIconsVisible, setIsIconsVisible] = useState(false);

    return (
        <div className={styles.parcelRow} onMouseEnter={() => setIsIconsVisible(true)} onMouseLeave={() => setIsIconsVisible(false)}>
            <input
                value={value}
                placeholder="custom shape"
                onChange={e => onChange(e.target.value)}
            />
            {!isSingle && (isIconsVisible || isChecked) && (
                <>
                    <Checkbox checked={isChecked} onChange={e => onToggle(e.target.checked)} />
                    <DeleteIcon fontSize="small" className={commonStyles.clickableIcon} onClick={onDelete} />
                </>
            )}
        </div>
    )
}

interface IParcelRowProps {
    value: string;
    onChange: (value: string) => void;
    isChecked: boolean;
    onToggle: (value: boolean) => void;
    isSingle: boolean;
    onDelete?: () => void;
}